import React, { useRef, useState, useEffect } from "react";
import { HexColorPicker, HexColorInput } from "react-colorful";
import classNames from "classnames";

export default function ColorPicker({
    color,
    onChange,
    className,
    alignment = "right",
    disabled
}: {
    color: string | undefined;
    onChange: ((color: string) => void) | undefined;
    className?: string;
    alignment?: "right" | "left";
    disabled?: boolean;
}) {
    const isDisabled = disabled ?? !onChange;
    const popover = useRef();
    const triggerRef = useRef();
    const [isOpen, setIsOpen] = useState(false);

    const [newColor, setNewColor] = useState(color);

    function close() {
        setIsOpen(false);
        // onChange is already called via the debounced useEffect, no need to call it again
    }

    useClickOutside(popover, close, isOpen && !isDisabled, triggerRef);

    useEffect(() => {
        setNewColor(color);
    }, [color]);

    // Debounced onChange to prevent infinite loops while still providing real-time updates
    useEffect(() => {
        if (isOpen && !isDisabled && onChange) {
            const timeoutId = setTimeout(() => {
                onChange(newColor);
            }, 50); // Small delay to debounce rapid changes

            return () => clearTimeout(timeoutId);
        }
    }, [newColor, isOpen, onChange, isDisabled]);

    return (
        <div
            className="relative"
            style={{
                transform: "translateZ(0)",
                isolation: "isolate",
                zIndex: isOpen ? 1000 : "auto"
            }}
        >
            <div
                ref={triggerRef}
                className={classNames(
                    isOpen && !isDisabled
                        ? "shadow-lg ring-black ring-opacity-30"
                        : "ring-black ring-opacity-30",
                    !isDisabled && "cursor-pointer hover:shadow-lg hover:ring-opacity-70",
                    "color h-3 w-3 rounded-full border-1 border-white ring-1 transition duration-200"
                )}
                style={{ backgroundColor: newColor }}
                onClick={(e) => {
                    if (isDisabled) return;
                    e.stopPropagation();
                    setIsOpen((prev) => !prev);
                }}
            />

            {isOpen && !isDisabled && (
                <div
                    className={classNames(
                        "z-[100] flex -translate-y-1/2 flex-col gap-2 rounded-lg border-1 border-gray-200 bg-white p-2 shadow-lg",
                        className,
                        alignment === "left" ? "-translate-x-56" : "translate-x-8"
                    )}
                    ref={popover}
                    style={{
                        overflow: "visible !important",
                        zIndex: 9999,
                        position: "fixed",
                        transform: "translateZ(0)",
                        isolation: "isolate"
                    }}
                >
                    <HexColorPicker color={newColor} onChange={setNewColor} />
                    <HexColorInput
                        color={newColor}
                        onChange={setNewColor}
                        placeholder="Type a color"
                        prefixed
                        alpha
                        className="input2"
                    />
                </div>
            )}
        </div>
    );
}

const useClickOutside = (ref, handler, isActive = true, triggerRef = null) => {
    useEffect(() => {
        if (!isActive) return;

        const handleClickOutside = (event) => {
            // Don't close if clicking on the trigger or inside the popover
            if (triggerRef && triggerRef.current && triggerRef.current.contains(event.target)) {
                return;
            }
            if (ref.current && !ref.current.contains(event.target)) {
                handler(event);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, handler, isActive, triggerRef?.current]);
};
